import { Injectable } from "@angular/core";
import { ContentValue } from "@fabric-types/svc-content";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { OpenIdConnectSelector } from "@obg/common.verification";
import { GtmLoginMethod, GtmRegistrationType } from "@obg/gtm.types/adaptive";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { CommonState } from "@obg/ngx.common";
import { Jurisdiction, JurisdictionSelector, SmartIdAuthTransactionSelector } from "@obg/ngx.core";
import { DocumentRef, TelemetryService } from "@obg/ngx.global.evo";
import { differenceInYears } from "date-fns";
import { head } from "lodash-es";
import { Observable } from "rxjs";
import { filter, map, mergeMap, take, tap, withLatestFrom } from "rxjs/operators";

import { REGISTRATION_ACTION_TYPE } from "../registration/registration.action";
import { RegistrationVerificationResponse } from "../registration/registration.model";
import { RegistrationSelector } from "../registration/registration.selector";
import { RegistrationService } from "../registration/registration.service";
import { RegistrationFeatureState } from "../state";

import { REGISTRATION_TELEMETRY_ACTION_TYPE } from "./registration-telemetry.action";
import {
	RegistrationTelemetryEventName,
	TelemetryErrorPayload,
	TelemetryEventPayload,
	TelemetryFieldEventPayload,
} from "./registration-telemetry.model";
import { getErrorMessage } from "./registration-telemetry.util";

@Injectable()
export class RegistrationTelemetryEffect {
	registrationFail$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					REGISTRATION_ACTION_TYPE.registrationFail,
					REGISTRATION_ACTION_TYPE.registrationFailWithSelfExclusion
				),
				map((x: ActionWithPayload<TelemetryErrorPayload>) => x.payload),
				concatLatestFrom(() => [
					this.store.select(JurisdictionSelector.get),
					this.store.select(this.oidcSelector.getToken()),
				]),
				tap(([error, jurisdiction]) => {
					const loginMethod = GtmLoginMethod.MitID;
					const registrationType = jurisdiction === Jurisdiction.Dga ? loginMethod : jurisdiction;
					let registrationMethod;
					if (
						jurisdiction === Jurisdiction.Iaui ||
						jurisdiction === Jurisdiction.Mta ||
						jurisdiction === Jurisdiction.Mga
					) {
						// registration method is always regular in these cases, but optimization team would still like to receive it
						registrationMethod = GtmRegistrationType.Regular;
					}

					this.telemetryService.track({
						event: RegistrationTelemetryEventName.FailedRegistration,
						Error: getErrorMessage(error),
						RegistrationType: registrationType,
						RegistrationMethod: registrationMethod,
					});
				})
			),
		{ dispatch: false }
	);

	registrationSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(REGISTRATION_ACTION_TYPE.registrationSuccess),
				withLatestFrom(this.store.select(this.registrationSelector.getState())),
				map(([, x]) => x),
				filter((registrationState) => {
					const { registrationDetails } = registrationState;
					return !!registrationDetails?.additionalDetails?.country;
				}),
				mergeMap((registrationState) =>
					new Observable(
						this.documentRef.obgEventBus.retrieve(ContentValue.getCountryByCode, {
							countryCode: registrationState.registrationDetails!.additionalDetails.country!,
						})
					).pipe(
						take(1),
						withLatestFrom(
							this.store.select(JurisdictionSelector.get),
							this.store.select(SmartIdAuthTransactionSelector.getAuthenticationToken)
						),
						tap(([country, jurisdiction, smartIdToken]) => {
							const { person, marketingOffers } = registrationState.registrationDetails!;
							const affiliateData = head(this.registrationService.getAffiliateMetaData())!;

							const registrationMethod =
								smartIdToken && person.lastName === ""
									? GtmRegistrationType.SmartID
									: GtmRegistrationType.Regular;

							const calculatedAge = person.dateOfBirth
								? differenceInYears(Date.now(), new Date(person.dateOfBirth)).toString()
								: "";

							const event: Record<string, any> = {
								event: RegistrationTelemetryEventName.ConfirmedRegistration,
								HashedEmail: registrationState.hashedEmail || "Missing",
								GUID: registrationState.token,
								Age: calculatedAge,
								Sex: person.gender,
								Location: country?.name,
								PartnerID: affiliateData?.affiliate,
								RegistrationType: jurisdiction,
								RegistrationMethod: registrationMethod,
							};

							if (jurisdiction === Jurisdiction.Ukgc) {
								event.PhoneNotificationToggle = marketingOffers.acceptTelesalesOffers;
								event.SMSNotificationToggle = marketingOffers.acceptSmsOffers;
								event.EmailNotificationToggle = marketingOffers.acceptEmailOffers;
							}

							this.telemetryService.track(event);
						})
					)
				)
			),
		{ dispatch: false }
	);

	registrationVerificationSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(REGISTRATION_ACTION_TYPE.verifyRegistrationTokenSuccess),
				map((x: ActionWithPayload<RegistrationVerificationResponse>) => x.payload),
				withLatestFrom(this.store.select(JurisdictionSelector.get)),
				tap(([registrationVerificationResponse, jurisdiction]) =>
					this.telemetryService.track({
						event: RegistrationTelemetryEventName.VerifiedRegistration,
						GUID: registrationVerificationResponse.customerId,
						RegistrationType: jurisdiction,
					})
				)
			),
		{ dispatch: false }
	);

	registrationUiInteractionEvent$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(REGISTRATION_TELEMETRY_ACTION_TYPE.uiInteractionEvent),
				map((x: ActionWithPayload<TelemetryEventPayload>) => x.payload),
				withLatestFrom(this.store.select(JurisdictionSelector.get)),
				tap(([payload, jurisdiction]) =>
					this.telemetryService.track({
						event: payload.event,
						RegistrationType: payload.registrationType || jurisdiction,
						Status: payload.status,
						RegistrationMethod: payload.registrationMethod,
					})
				)
			),
		{ dispatch: false }
	);

	registrationUiInteractionFieldEvent$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(REGISTRATION_TELEMETRY_ACTION_TYPE.uiInteractionFieldEvent),
				map((x: ActionWithPayload<TelemetryFieldEventPayload>) => x.payload),
				withLatestFrom(this.store.select(JurisdictionSelector.get)),
				tap(([payload, jurisdiction]) => {
					const trackingData = {
						event: RegistrationTelemetryEventName.RegistrationFieldChange,
						FieldName: payload.field,
						RegistrationType: payload.registrationType || jurisdiction,
						Jurisdiction: payload.field === "Country" ? jurisdiction?.toUpperCase() : undefined,
						RegistrationMethod: payload.registrationMethod,
					} as Record<string, string>;
					if (payload.registrationStep) {
						trackingData.RegistrationStep = `Step ${payload.registrationStep}`;
					}
					this.telemetryService.track(trackingData);
				})
			),
		{ dispatch: false }
	);

	constructor(
		private actions$: Actions,
		private telemetryService: TelemetryService,
		private store: Store<RegistrationFeatureState & CommonState>,
		private registrationSelector: RegistrationSelector,
		private registrationService: RegistrationService,
		private oidcSelector: OpenIdConnectSelector,
		private documentRef: DocumentRef
	) {}
}
